import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  HStack,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useDisclosure,
  useColorModeValue,
  Container,
  Image,
} from "@chakra-ui/react";
import { ChevronDownIcon, HamburgerIcon } from "@chakra-ui/icons";
import { useScrollLock } from "./hooks/useScrollLock";
import ServiceMegaMenu from "./ServiceMegaMenu";
import ProductsMegaMenu from "./ProductsMegaMenu";
import MobileDrawer from "./MobileDrawer";
import { useNavigate, useLocation } from "react-router-dom";

// careers special nav
const getCareersNav = (navigate) => [
  { name: "Job Opening", path: "/job-opening", onClick: () => navigate("/job-opening") },
  { name: "Internship", path: "/internship", onClick: () => navigate("/internship") },
  { name: "Skill Development", path: "/skill-development", onClick: () => navigate("/skill-development") },
  { name: "Team Activity", path: "/team-activity", onClick: () => navigate("/team-activity") },
  { name: "Join Us", path: "/join-us", onClick: () => navigate("/join-us") },
];

const navigationItems = [
  {
    name: "Services",
    hasDropdown: true,
    isMegaMenu: true,
    menuType: "service",
    dropdownItems: ["Web Development", "Mobile Apps", "Consulting", "Support"],
  },
  {
    name: "Products",
    hasDropdown: true,
    isMegaMenu: true,
    menuType: "products",
    dropdownItems: [
      "Software Solutions",
      "Enterprise Tools",
      "Analytics",
      "Integration",
    ],
  },
  {
    name: "About Us",
    hasDropdown: false,
    onClick: (navigate) => navigate("/about-us"),
  },
  // {
  //   name: "Our Culture",
  //   hasDropdown: false,
  //   onClick: (navigate) => navigate("/our-culture"),
  // },
  {
    name: "Career",
    hasDropdown: false,
    onClick: (navigate) => navigate("/careers"),
  },
  {
    name: "Newsroom",
    hasDropdown: false,
    onClick: (navigate) => navigate("/newsroom"),
  },
  {
    name: "Contact",
    hasDropdown: false,
    onClick: (navigate) => navigate("/contact"),
  },
];

const Header = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // State variables for controlling hover menus with timeout refs
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const serviceTimeoutRef = React.useRef(null);
  const productTimeoutRef = React.useRef(null);
  
  const navigate = useNavigate();
  const location = useLocation();



  // Lock scroll when any mega menu is open
  useScrollLock(serviceMenuOpen || productMenuOpen);

  // Close mega menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if ((serviceMenuOpen || productMenuOpen) && !event.target.closest('[data-mega-menu]')) {
        setServiceMenuOpen(false);
        setProductMenuOpen(false);
      }
    };

    if (serviceMenuOpen || productMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [serviceMenuOpen, productMenuOpen]);

  // Clear timeout refs on unmount
  useEffect(() => {
    return () => {
      if (serviceTimeoutRef.current) {
        clearTimeout(serviceTimeoutRef.current);
      }
      if (productTimeoutRef.current) {
        clearTimeout(productTimeoutRef.current);
      }
    };
  }, []);

  // Improved hover handlers with delay
  const handleServiceMouseEnter = () => {
    if (serviceTimeoutRef.current) {
      clearTimeout(serviceTimeoutRef.current);
    }
    setServiceMenuOpen(true);
    setProductMenuOpen(false);
  };

  const handleServiceMouseLeave = () => {
    serviceTimeoutRef.current = setTimeout(() => {
      setServiceMenuOpen(false);
    }, 150); // 150ms delay
  };

  const handleProductMouseEnter = () => {
    if (productTimeoutRef.current) {
      clearTimeout(productTimeoutRef.current);
    }
    setProductMenuOpen(true);
    setServiceMenuOpen(false);
  };

  const handleProductMouseLeave = () => {
    productTimeoutRef.current = setTimeout(() => {
      setProductMenuOpen(false);
    }, 150); // 150ms delay
  };

  const handleMegaMenuMouseEnter = (type) => {
    // Cancel any pending close timeouts when entering the mega menu
    if (type === 'service' && serviceTimeoutRef.current) {
      clearTimeout(serviceTimeoutRef.current);
    }
    if (type === 'product' && productTimeoutRef.current) {
      clearTimeout(productTimeoutRef.current);
    }
  };

  const handleMegaMenuMouseLeave = (type) => {
    // Set the appropriate menu to close when leaving the mega menu
    if (type === 'service') {
      serviceTimeoutRef.current = setTimeout(() => {
        setServiceMenuOpen(false);
      }, 150);
    }
    if (type === 'product') {
      productTimeoutRef.current = setTimeout(() => {
        setProductMenuOpen(false);
      }, 150);
    }
  };

  const toggleMobileDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };



  const textColor = useColorModeValue("gray.700", "gray.200");
  const hoverColor = useColorModeValue("#000B8C", "blue.400");

  // careers page detect
  const isCareersPage = [
    "/careers",
    "/join-us",
    "/team-activity",
    "/job-opening",
    "/job-details",
    "/internship",
    "/skill-development",
    "/course-details",
  ].some((path) => location.pathname.startsWith(path));
  const careersNav = getCareersNav(navigate);

  return (
    <>
      <Box>
        <Container
          maxW={{ base: "100vw", md: "7xl" }}
          px={{ base: 3, sm: 4, md: 6 }}
          overflowX="visible"
        >
          <Flex
            h="auto"
            minH={{ base: "60px", md: "70px" }}
            py={{ base: 2, md: 3 }}
            alignItems="center"
            w="full"
            justify="space-between"
          >
            {/* Logo */}
            <Box
              as="button"
              onClick={() => navigate("/")}
              _focus={{ outline: "none" }}
              flexShrink={0}
              minW="fit-content"
              zIndex={10}
            >
              <Image
                src="/png/BA.png"
                alt="Company Logo"
                height={{ base: "32px", sm: "36px", md: "40px" }}
                maxWidth="100%"
                width="auto"
                objectFit="contain"
              />
            </Box>

            {/* Desktop Navigation */}
            <HStack 
              display={{ base: "none", lg: "flex" }} 
              alignItems="center"
              spacing={{ base: 0, lg: 1, xl: 2 }}
              flex="1"
              justify="flex-end"
            >
              {isCareersPage ? (
                <>
                  {careersNav.map((item, idx) => (
                    <Button
                      key={item.name}
                      variant="ghost"
                      color={location.pathname.startsWith(item.path) ? "#234F1E" : textColor}
                      fontWeight={location.pathname.startsWith(item.path) ? "bold" : "medium"}
                      fontSize="md"
                      _hover={{ color: "#234F1E" }}
                      onClick={item.onClick}
                      position="relative"
                      sx={{
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: "0",
                          left: "0",
                          width: location.pathname.startsWith(item.path) ? "100%" : "0%",
                          height: "2px",
                          backgroundColor: "#215560",
                          transition: "width 0.3s ease",
                        },
                        "&:hover::after": {
                          width: "100%",
                        },
                      }}
                    >
                      {item.name}
                    </Button>
                  ))}
                  {/* <Button
                    variant="outline"
                    borderColor="#234F1E"
                    color="#234F1E"
                    borderRadius="full"
                    px={6}
                    fontWeight="medium"
                    fontSize="md"
                    _hover={{ bg: "#234F1E", color: "white" }}
                    ml={4}
                    onClick={() => navigate("/login-register?mode=login")}
                  >
                    Login
                  </Button> */}
                  {/* <Button
                    bg="#234F1E"
                    color="white"
                    borderRadius="full"
                    px={{ base: 3, sm: 5, md: 7 }}
                    fontWeight="bold"
                    fontSize={{ base: "xs", sm: "sm", md: "md" }}
                    minW={{ base: "80px", sm: "100px", md: "120px" }}
                    whiteSpace="normal"
                    _hover={{ bg: "#1a3d17" }}
                    ml={2}
                    onClick={() => navigate("/login-register?mode=register")}
                  >
                    Register
                  </Button> */}
                </>
              ) : (
                <>
                  {/* Service Menu - Exactly like Queue Pro */}
                  <Menu isOpen={serviceMenuOpen} isLazy>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    variant="ghost"
                    color={serviceMenuOpen ? hoverColor : textColor}
                    fontWeight="medium"
                    fontSize={{ base: "sm", lg: "md" }}
                    size={{ base: "sm", lg: "md" }}
                    px={{ base: 3, md: 4, lg: 5 }}
                    py={{ base: 3, md: 4 }}
                    minW="fit-content"
                    _hover={{ color: hoverColor, bg: "gray.50" }}
                    _active={{ bg: "gray.50" }}
                    whiteSpace="nowrap"
                    borderRadius="md"
                    transition="all 0.2s ease"
                    textDecoration="none"
                    _focus={{ boxShadow: "none", outline: "none" }}
                    onMouseEnter={handleServiceMouseEnter}
                    onMouseLeave={handleServiceMouseLeave}
                  >
                    Services
                  </MenuButton>
                  <ServiceMegaMenu
                    isOpen={serviceMenuOpen}
                    onClose={() => setServiceMenuOpen(false)}
                    onMouseEnter={() => handleMegaMenuMouseEnter('service')}
                    onMouseLeave={() => handleMegaMenuMouseLeave('service')}
                  />
                </Menu>

                {/* Products Menu - Exactly like Queue Pro */}
                <Menu isOpen={productMenuOpen} isLazy>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    variant="ghost"
                    color={productMenuOpen ? hoverColor : textColor}
                    fontWeight="medium"
                    fontSize={{ base: "sm", lg: "md" }}
                    size={{ base: "sm", lg: "md" }}
                    px={{ base: 3, md: 4, lg: 5 }}
                    py={{ base: 3, md: 4 }}
                    minW="fit-content"
                    _hover={{ color: hoverColor, bg: "gray.50" }}
                    _active={{ bg: "gray.50" }}
                    whiteSpace="nowrap"
                    borderRadius="md"
                    transition="all 0.2s ease"
                    textDecoration="none"
                    _focus={{ boxShadow: "none", outline: "none" }}
                    onMouseEnter={handleProductMouseEnter}
                    onMouseLeave={handleProductMouseLeave}
                  >
                    Products
                  </MenuButton>
                  <ProductsMegaMenu
                    isOpen={productMenuOpen}
                    onClose={() => setProductMenuOpen(false)}
                    onMouseEnter={() => handleMegaMenuMouseEnter('product')}
                    onMouseLeave={() => handleMegaMenuMouseLeave('product')}
                  />
                </Menu>

                {/* Other navigation items */}
                {navigationItems.filter(item => !item.hasDropdown || !item.isMegaMenu).map((item, index) => {
                  if (item.hasDropdown) {
                    return (
                      <Box key={index} flexShrink={0}>
                        <Menu>
                          <MenuButton
                            as={Button}
                            rightIcon={<ChevronDownIcon />}
                            variant="ghost"
                            color={textColor}
                            fontWeight="medium"
                            fontSize={{ base: "sm", lg: "md" }}
                            size={{ base: "sm", lg: "md" }}
                            px={{ base: 2, md: 3, lg: 4 }}
                            py={{ base: 2, md: 3 }}
                            minW="fit-content"
                            _hover={{ color: hoverColor }}
                            _active={{ bg: "gray.50" }}
                            whiteSpace="nowrap"
                          >
                            {item.name}
                          </MenuButton>
                          <MenuList zIndex="dropdown">
                            {item.dropdownItems?.map(
                              (dropdownItem, dropdownIndex) => (
                                <MenuItem
                                  key={dropdownIndex}
                                  _hover={{ color: hoverColor, bg: "gray.50" }}
                                  fontSize={{ base: "sm", md: "md" }}
                                >
                                  {dropdownItem}
                                </MenuItem>
                              )
                            )}
                          </MenuList>
                        </Menu>
                      </Box>
                    );
                  } else {
                    return (
                      <Box key={index} flexShrink={0}>
                        <Button
                          variant="ghost"
                          color={textColor}
                          fontWeight="medium"
                          fontSize={{ base: "sm", lg: "md" }}
                          size={{ base: "sm", lg: "md" }}
                          px={{ base: 2, md: 3, lg: 4 }}
                          py={{ base: 2, md: 3 }}
                          minW="fit-content"
                          _hover={{ color: hoverColor }}
                          whiteSpace="nowrap"
                          onClick={
                            item.onClick
                              ? () => item.onClick(navigate)
                              : undefined
                          }
                        >
                          {item.name}
                        </Button>
                      </Box>
                    );
                  }
                })}
                </>
              )}
            </HStack>

            {/* Mobile menu button */}
            <IconButton
              size={{ base: "md", sm: "lg" }}
              icon={<HamburgerIcon />}
              aria-label="Open Menu"
              display={{ lg: "none" }}
              onClick={onOpen}
              variant="ghost"
              color={textColor}
              _hover={{ color: hoverColor, bg: "gray.100" }}
              _active={{ bg: "gray.200" }}
              flexShrink={0}
              minW="fit-content"
              ml="auto"
            />
          </Flex>
        </Container>

        {/* Mobile Navigation Drawer */}
        <MobileDrawer
          isOpen={isOpen}
          onClose={onClose}
          activeDropdown={activeDropdown}
          toggleMobileDropdown={toggleMobileDropdown}
          isCareersPage={isCareersPage}
          careersNav={careersNav}
          navigationItems={navigationItems}
          textColor={textColor}
          hoverColor={hoverColor}
        />
      </Box>
    </>
  );
};

export default Header;
